import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  TextField,
  InputAdornment,
  Link,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
  Calculate as CalculateIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  portfolioService,
  Holding,
  HoldingsResponse,
  StockTransaction,
  StockTransactionsResponse,
} from '../../services/portfolioService';

interface OpenPositionsProps {
  portfolioId: number;
  portfolioName: string;
}

const OpenPositions: React.FC<OpenPositionsProps> = ({ portfolioId, portfolioName }) => {
  const navigate = useNavigate();
  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [holdingsWithCurrentValue, setHoldingsWithCurrentValue] = useState<Array<Holding & {
    current_price?: number;
    current_value?: number;
    profit_loss?: number;
    profit_loss_percentage?: number;
  }>>([]);
  const [summary, setSummary] = useState<{
    total_holdings: number;
    total_invested: number;
    total_quantity: number;
    total_shares?: number;
    portfolio_average_price?: number;
  } | null>(null);
  const [realInvestmentData, setRealInvestmentData] = useState<{
    total_nominal_investment: number;
    total_real_investment: number;
    cost_of_money_impact: number;
    annual_rate_used: number;
    calculation_date: string;
  } | null>(null);
  const [portfolioCurrentValue, setPortfolioCurrentValue] = useState<{
    portfolio_value: number;
    total_investment: number;
    profit_loss: number;
    profit_loss_percentage: number;
    last_updated: string;
  } | null>(null);
  const [costOfMoneyRate, setCostOfMoneyRate] = useState<number>(8.0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Stock transactions dialog
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<string>('');
  const [stockTransactions, setStockTransactions] = useState<StockTransaction[]>([]);
  const [stockInfo, setStockInfo] = useState<{
    symbol: string;
    name: string;
    exchange: string;
  } | null>(null);
  const [currentHolding, setCurrentHolding] = useState<{
    quantity: number;
    invested_amount: number;
    average_price: number;
  } | null>(null);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  const calculateRealInvestment = useCallback(async (rate: number) => {
    try {
      setIsCalculating(true);
      const summaryResponse = await portfolioService.getPortfolioSummaryWithRate(portfolioId, rate);
      
      if (summaryResponse.real_investment_data) {
        setRealInvestmentData(summaryResponse.real_investment_data);
      }
      
      // Update summary with total_shares and portfolio_average_price from summary response
      setSummary(prev => prev ? {
        ...prev,
        total_shares: summaryResponse.total_shares || 0,
        portfolio_average_price: summaryResponse.portfolio_average_price || 0
      } : prev);
    } catch (error: any) {
      console.error('Failed to calculate real investment:', error);
      toast.error('Failed to calculate real investment');
    } finally {
      setIsCalculating(false);
    }
  }, [portfolioId]);

  const loadPortfolioCurrentValue = useCallback(async () => {
    try {
      const response = await portfolioService.getCurrentValue(portfolioId);
      setPortfolioCurrentValue(response);
    } catch (error: any) {
      console.error('Failed to load portfolio current value:', error);
      // Don't show error toast as this is optional data
    }
  }, [portfolioId]);

  const loadHoldings = useCallback(async () => {
    try {
      setLoading(true);
      const holdingsResponse = await portfolioService.getHoldings(portfolioId, costOfMoneyRate);
      
      setHoldings(holdingsResponse.holdings);
      setSummary({
        total_holdings: holdingsResponse.total_holdings,
        total_invested: holdingsResponse.total_invested,
        total_quantity: holdingsResponse.total_quantity,
      });
      
      // Calculate real investment and get portfolio metrics with current rate
      await calculateRealInvestment(costOfMoneyRate);
      
      // Load current portfolio value with live prices
      await loadPortfolioCurrentValue();
      
      // Merge holdings with current value data
      const currentValueResponse = await portfolioService.getCurrentValue(portfolioId);
      if (currentValueResponse && currentValueResponse.holdings_with_prices) {
        const mergedHoldings = holdingsResponse.holdings.map(holding => {
          const currentValueData = currentValueResponse.holdings_with_prices.find(
            (h: any) => h.symbol === holding.symbol
          );
          return {
            ...holding,
            current_price: currentValueData?.current_price || 0,
            current_value: currentValueData?.current_value || holding.invested_amount,
            profit_loss: currentValueData?.profit_loss || 0,
            profit_loss_percentage: currentValueData?.profit_loss_percentage || 0
          };
        });
        setHoldingsWithCurrentValue(mergedHoldings);
      } else {
        // Fallback: use invested amount as current value
        const fallbackHoldings = holdingsResponse.holdings.map(holding => ({
          ...holding,
          current_price: 0,
          current_value: holding.invested_amount,
          profit_loss: 0,
          profit_loss_percentage: 0
        }));
        setHoldingsWithCurrentValue(fallbackHoldings);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to load holdings');
      toast.error('Failed to load holdings');
    } finally {
      setLoading(false);
    }
  }, [portfolioId, costOfMoneyRate, calculateRealInvestment, loadPortfolioCurrentValue]);

  useEffect(() => {
    loadHoldings();
  }, [loadHoldings]);

  const loadStockTransactions = useCallback(async (stockSymbol: string) => {
    try {
      setTransactionsLoading(true);
      const response: StockTransactionsResponse = await portfolioService.getStockTransactions(
        portfolioId,
        stockSymbol,
        costOfMoneyRate
      );
      setStockTransactions(response.transactions);
      setStockInfo(response.stock);
      setCurrentHolding(response.current_holding);
    } catch (error: any) {
      toast.error('Failed to load stock transactions');
    } finally {
      setTransactionsLoading(false);
    }
  }, [portfolioId, costOfMoneyRate]);

  // Reload transactions when cost of money rate changes and dialog is open
  useEffect(() => {
    if (transactionsDialogOpen && selectedStock) {
      loadStockTransactions(selectedStock);
    }
  }, [costOfMoneyRate, transactionsDialogOpen, selectedStock, loadStockTransactions]);

  const handleViewTransactions = async (stockSymbol: string) => {
    setSelectedStock(stockSymbol);
    setTransactionsDialogOpen(true);
    await loadStockTransactions(stockSymbol);
  };

  const handleDownloadTransactionsCSV = () => {
    if (!stockTransactions.length) {
      toast.warning('No transactions to download');
      return;
    }

    // Create CSV content
    const headers = ['Date', 'Type', 'Quantity', 'Price', 'Total Value', 'Real Price', 'Cost of Money Impact', 'Source'];
    const rows = stockTransactions.map(txn => [
      new Date(txn.transaction_date).toLocaleDateString('en-IN'),
      txn.transaction_type,
      txn.quantity,
      txn.price.toFixed(2),
      txn.total_value.toFixed(2),
      txn.real_price ? txn.real_price.toFixed(2) : '',
      txn.cost_of_money_impact ? txn.cost_of_money_impact.toFixed(2) : '',
      txn.source
    ]);

    // Add summary row
    if (currentHolding) {
      rows.push([]); // Empty row
      rows.push(['Summary']);
      rows.push(['Current Quantity', currentHolding.quantity.toString()]);
      rows.push(['Average Price', currentHolding.average_price.toFixed(2)]);
      rows.push(['Invested Amount', currentHolding.invested_amount.toFixed(2)]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedStock}_transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Transactions downloaded successfully');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (value: number, decimals: number = 2) => {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: decimals,
    }).format(value);
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case 'BUY':
        return 'success';
      case 'SELL':
        return 'error';
      case 'BONUS':
        return 'warning';
      case 'OPEN_POSITION':
        return 'info';
      default:
        return 'default';
    }
  };

  const transactionColumns: GridColDef[] = [
    {
      field: 'transaction_type',
      headerName: 'Type',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getTransactionTypeColor(params.value as string) as any}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      width: 100,
      type: 'number',
      valueFormatter: (params) => formatNumber(params.value as number, 0),
    },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      type: 'number',
      valueFormatter: (params) => formatCurrency(params.value as number),
    },
    {
      field: 'total_value',
      headerName: 'Total Value',
      width: 140,
      type: 'number',
      valueFormatter: (params) => formatCurrency(params.value as number),
    },
    {
      field: 'real_price',
      headerName: 'Real Price',
      width: 140,
      type: 'number',
      valueFormatter: (params) => params.value ? formatCurrency(params.value as number) : '-',
      renderCell: (params) => {
        if (!params.value) return <Typography variant="body2">-</Typography>;
        const costImpact = params.row.cost_of_money_impact || 0;
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {formatCurrency(params.value as number)}
            </Typography>
            {costImpact > 0 && (
              <Typography variant="caption" color="text.secondary">
                +{formatCurrency(costImpact)}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 120,
      valueFormatter: (params) => new Date(params.value as string).toLocaleDateString('en-IN'),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          variant="outlined"
          size="small"
          color={params.value === 'MANUAL' ? 'primary' : 'default'}
        />
      ),
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Compact Controls Row */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            label="Cost of Money Rate"
            type="number"
            size="small"
            value={costOfMoneyRate}
            onChange={(e) => {
              const newRate = parseFloat(e.target.value) || 0;
              setCostOfMoneyRate(newRate);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const newRate = parseFloat((e.target as HTMLInputElement).value) || 0;
                if (newRate >= 0 && newRate <= 100) {
                  calculateRealInvestment(newRate);
                }
              }
            }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { step: 0.1, min: 0, max: 100 }
            }}
            sx={{ width: 120 }}
            disabled={isCalculating}
          />
          {isCalculating && <CircularProgress size={16} />}
        </Box>
        <Button variant="outlined" size="small" onClick={loadHoldings}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Compact Summary Stats - Single Row */}
      {summary && (
        <Box 
          display="flex" 
          gap={1} 
          mb={1.5} 
          flexWrap="wrap"
          sx={{ 
            '& > *': { 
              flex: '1 1 auto',
              minWidth: 140 
            } 
          }}
        >
          {/* Total Holdings */}
          <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
              <Typography color="textSecondary" variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
                Total Holdings
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                {summary.total_holdings}
              </Typography>
            </CardContent>
          </Card>

          {/* Investment Amount */}
          <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
              <Typography color="textSecondary" variant="caption" sx={{ display: 'block', lineHeight: 1.2 }}>
                Investment Amount
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
                {formatCurrency(summary.total_invested)}
              </Typography>
            </CardContent>
          </Card>

          {/* Real Investment */}
          {realInvestmentData && (
            <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="inherit" variant="caption" sx={{ display: 'block', lineHeight: 1.2, opacity: 0.9 }}>
                  Real Investment
                </Typography>
                <Typography variant="h6" color="inherit" sx={{ lineHeight: 1.3 }}>
                  {formatCurrency(realInvestmentData.total_real_investment)}
                </Typography>
                <Typography variant="caption" color="inherit" sx={{ opacity: 0.85, fontSize: '0.65rem' }}>
                  +{formatCurrency(realInvestmentData.cost_of_money_impact)} cost
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Portfolio Value */}
          {portfolioCurrentValue && (
            <Card sx={{ bgcolor: 'success.main', color: 'success.contrastText' }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="inherit" variant="caption" sx={{ display: 'block', lineHeight: 1.2, opacity: 0.9 }}>
                  Portfolio Value
                </Typography>
                <Typography variant="h6" color="inherit" sx={{ lineHeight: 1.3 }}>
                  {formatCurrency(portfolioCurrentValue.portfolio_value)}
                </Typography>
                <Typography variant="caption" color="inherit" sx={{ opacity: 0.85, fontSize: '0.65rem' }}>
                  Current Market Value
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* P&L */}
          {portfolioCurrentValue && (
            <Card sx={{ 
              bgcolor: portfolioCurrentValue.profit_loss >= 0 ? 'success.main' : 'error.main', 
              color: portfolioCurrentValue.profit_loss >= 0 ? 'success.contrastText' : 'error.contrastText' 
            }}>
              <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                <Typography color="inherit" variant="caption" sx={{ display: 'block', lineHeight: 1.2, opacity: 0.9 }}>
                  P&L
                </Typography>
                <Typography variant="h6" color="inherit" sx={{ lineHeight: 1.3 }}>
                  {portfolioCurrentValue.profit_loss >= 0 ? '' : '-'}₹{Math.abs(portfolioCurrentValue.profit_loss).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </Typography>
                <Typography variant="caption" color="inherit" sx={{ opacity: 0.85, fontSize: '0.65rem' }}>
                  ({portfolioCurrentValue.profit_loss_percentage >= 0 ? '+' : ''}{portfolioCurrentValue.profit_loss_percentage.toFixed(2)}%)
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      )}

      {/* Holdings Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { py: 1 } }}>
                <TableCell><Typography variant="body2" fontWeight="bold">Stock</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">Quantity</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">Avg. Price</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">Invested Amount</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">Current Value</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">P&L</Typography></TableCell>
                <TableCell align="right"><Typography variant="body2" fontWeight="bold">Real Investment</Typography></TableCell>
                <TableCell align="center"><Typography variant="body2" fontWeight="bold">Transactions</Typography></TableCell>
                <TableCell align="center"><Typography variant="body2" fontWeight="bold">Actions</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {holdingsWithCurrentValue.map((holding) => (
                <TableRow key={holding.symbol} hover sx={{ '& td': { py: 0.5 } }}>
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" gap={0.25}>
                        <Link
                          component="button"
                          variant="body2"
                          onClick={() => navigate(`/analysis?symbol=${encodeURIComponent(holding.symbol)}`)}
                          sx={{ fontWeight: 'bold', textDecoration: 'none', cursor: 'pointer' }}
                        >
                          {holding.symbol}
                        </Link>
                        <Tooltip title="Screener.in">
                          <IconButton size="small" component="a" href={`https://www.screener.in/company/${holding.symbol.replace(/\.(NS|BO)$/, '')}/consolidated/`} target="_blank" rel="noopener" sx={{ p: 0 }}>
                            <OpenInNewIcon sx={{ fontSize: '0.75rem', color: 'text.disabled' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      {holding.current_price && holding.current_price > 0 && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          @ {formatCurrency(holding.current_price)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatNumber(holding.quantity, 0)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {formatCurrency(holding.average_price)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(holding.invested_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency(holding.current_value || holding.invested_amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box>
                      <Typography 
                        variant="body2" 
                        fontWeight="bold"
                        color={
                          (holding.profit_loss || 0) >= 0 ? 'success.main' : 'error.main'
                        }
                      >
                        {(holding.profit_loss || 0) >= 0 ? '+' : ''}{formatCurrency(holding.profit_loss || 0)}
                      </Typography>
                      {holding.profit_loss_percentage !== undefined && (
                        <Typography 
                          variant="caption" 
                          color={
                            holding.profit_loss_percentage >= 0 ? 'success.main' : 'error.main'
                          }
                        >
                          ({holding.profit_loss_percentage >= 0 ? '+' : ''}{holding.profit_loss_percentage.toFixed(1)}%)
                    </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {formatCurrency(holding.real_investment)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" gap={0.5} justifyContent="center">
                      {holding.buy_transactions > 0 && (
                        <Chip
                          label={`${holding.buy_transactions} Buy`}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {holding.sell_transactions > 0 && (
                        <Chip
                          label={`${holding.sell_transactions} Sell`}
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {holding.bonus_transactions > 0 && (
                        <Chip
                          label={`${holding.bonus_transactions} Bonus`}
                          color="warning"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleViewTransactions(holding.symbol)}
                      color="primary"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {holdings.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No open positions found
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Upload transactions or add manual transactions to see your holdings here.
          </Typography>
        </Box>
      )}

      {/* Stock Transactions Dialog */}
      <Dialog
        open={transactionsDialogOpen}
        onClose={() => setTransactionsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {stockInfo ? (
            <Box>
              <Typography variant="h6">
                {stockInfo.symbol} - Transaction History
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {stockInfo.name} ({stockInfo.exchange})
              </Typography>
            </Box>
          ) : (
            `${selectedStock} - Transaction History`
          )}
        </DialogTitle>
        <DialogContent>
          {transactionsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {currentHolding && (
                <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                  <Typography variant="h6" gutterBottom>
                    Current Holding Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Quantity
                      </Typography>
                      <Typography variant="h6">
                        {formatNumber(currentHolding.quantity, 0)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Average Price
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(currentHolding.average_price)}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2" color="textSecondary">
                        Invested Amount
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(currentHolding.invested_amount)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}
              
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={stockTransactions}
                  columns={transactionColumns}
                  initialState={{
                    pagination: {
                      paginationModel: { page: 0, pageSize: 10 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  disableRowSelectionOnClick
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDownloadTransactionsCSV}
            startIcon={<DownloadIcon />}
            color="primary"
            disabled={stockTransactions.length === 0}
          >
            Download CSV
          </Button>
          <Button onClick={() => setTransactionsDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OpenPositions; 